import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"

import { cn } from "@/lib/utils"

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>
>(({ className, value, max = 100, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    // `value` also goes to the Root, not only to the indicator's transform:
    // without it Radix reports `data-state="indeterminate"` and emits no
    // `aria-valuenow`, so an assistive technology hears a progress bar that
    // never says how far along it is.
    //
    // CLAMPED, because several callers pass a real percentage that legitimately
    // exceeds the range — an over-budget project, an over-received revenue
    // source, a refund-negative budget line. Radix treats an out-of-range value
    // as invalid: it logs on every render and falls back to `indeterminate`,
    // which would be worse than before for exactly the bars that carry the most
    // information. The indicator clamps the same way, so 137% reads as a full
    // bar rather than one that has run off the end.
    value={value == null ? undefined : Math.min(Math.max(value, 0), max)}
    max={max}
    className={cn(
      "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
      className
    )}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className="h-full w-full flex-1 bg-primary transition-all"
      style={{
        transform: `translateX(-${100 - Math.min(Math.max(((value || 0) / max) * 100, 0), 100)}%)`,
      }}
    />
  </ProgressPrimitive.Root>
))
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
